import { useCallback, useMemo, useState } from 'react';
import { PostsBoardFile } from '../postsModel';

export interface PostsSelection {
  isOn: boolean;
  start: () => void;
  stop: () => void;
  isPicked: (file: PostsBoardFile) => boolean;
  toggle: (file: PostsBoardFile) => void;
  pickAll: () => void;
  picked: PostsBoardFile[];
}

/**
 * "Select" of the Posts page (design assets-rethink c1): the checkboxes in every lane and the bar
 * "N picked · Pick all N · Archive N". A long press on a phone (c3) starts it with that file.
 * `files` are the files that can be picked now; a file that left the board leaves the selection.
 */
export function usePostsSelection(files: ReadonlyArray<PostsBoardFile>): PostsSelection {
  const [isOn, setIsOn] = useState(false);
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set());

  const start = useCallback(() => setIsOn(true), []);
  const stop = useCallback(() => {
    setIsOn(false);
    setKeys(new Set());
  }, []);
  const toggle = useCallback((file: PostsBoardFile) => {
    setIsOn(true);
    setKeys((current) => {
      const next = new Set(current);
      if (next.has(file.key)) {
        next.delete(file.key);
      } else {
        next.add(file.key);
      }
      return next;
    });
  }, []);
  const pickAll = useCallback(() => setKeys(new Set(files.map((file) => file.key))), [files]);
  const isPicked = useCallback((file: PostsBoardFile) => keys.has(file.key), [keys]);
  const picked = useMemo(() => files.filter((file) => keys.has(file.key)), [files, keys]);

  return { isOn, start, stop, isPicked, toggle, pickAll, picked };
}
